import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SimplePageLayout } from "../../components/simplePage/simplePageLayout.tsx";
import { DataGrid, type DataGridHandle } from "../../components/dataGrid/dataGrid.tsx";
import { GridEditModal } from "../../components/editSearch/gridEditModal.tsx";
import { useGridEditDialog } from "../../hooks/useGridEditDialog.ts";
import {
  getPromotionInfo, savePromotionInfo, getPromotionLists,
  emptyPromotion, PURCHASE_TYPE_UNSET, type Promotion, type PromotionList,
} from "../../api/promotion.ts";
import { useAuth } from "../../auth/authContext.tsx";
import { useToast } from "../../hooks/useToast.ts";
import { OperatorSinglePicker } from "../../components/ui/operatorSinglePicker.tsx";
import formStyles from "../../styles/formPage.module.scss";
import {
  VALID_TIMES_FOR_CODE, VALID_TIMES_FOR_FIRST_TIME, type Option,
  getValidTimesForOptions, getAppliesToOptions, getGivesAsOptions, getDiscountOnOptions,
  showExtendedDays, useCounterForMinCart, normalizePromotion, parseValidDays, validatePromotion, snapshot,
} from "./promotionRules.ts";

/* ── Module-specific options ─────────────────────────── */

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface Status { type: "success" | "error"; message: string }

type ListsState = "loading" | "ready" | "failed";

/**
 * `<select>` over numeric options that always shows the value state holds. A
 * plain select whose value matches no option paints its first option as if it
 * were chosen while state keeps the other value, so the form showed one thing
 * and saved another. An unmatched value here gets its own disabled option
 * instead, which validation then refuses, so the user sees what is wrong.
 */
function NumberSelect({ value, options, onChange, placeholder, unknownLabel }: {
  value: number;
  options: Option[];
  onChange: (v: number) => void;
  /** "Nothing chosen yet" entry; disabled, so it can't be re-picked. */
  placeholder?: Option;
  unknownLabel?: (v: number) => string;
}) {
  const known = placeholder?.v === value || options.some((o) => o.v === value);
  return (
    <select className={formStyles.fieldSelect} value={value}
      onChange={(e) => onChange(parseInt(e.target.value, 10))}>
      {placeholder && <option value={placeholder.v} disabled>{placeholder.l}</option>}
      {!known && (
        <option value={value} disabled>
          {unknownLabel ? unknownLabel(value) : `Unknown value (${value})`}
        </option>
      )}
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

/* ── Shared promotion form fields ─────────────────────── */

interface PromotionFormProps {
  promo: Promotion;
  setField: <K extends keyof Promotion>(key: K, value: Promotion[K]) => void;
  isNew: boolean;
  moduleId: number;
  ownerOperatorId: number | null | undefined;
  lists: PromotionList[];
  listsState: ListsState;
  locationMode: "kiosk" | "list";
  setLocationMode: (m: "kiosk" | "list") => void;
}

function PromotionForm({ promo, setField, isNew, moduleId, ownerOperatorId, lists, listsState, locationMode, setLocationMode }: PromotionFormProps) {
  const productListOpts = useMemo<Option[]>(() => [
    { v: 0, l: "-- None --" },
    ...lists.filter((l) => l.sType === "PROD").map((l) => ({ v: l.iPromotionListID, l: l.sListName })),
  ], [lists]);
  // 0 is saved as NULL, which the kiosk sync reads as "every location".
  const locationListOpts = useMemo<Option[]>(() => [
    { v: 0, l: "-- All locations --" },
    ...lists.filter((l) => l.sType !== "PROD").map((l) => ({ v: l.iPromotionListID, l: l.sListName })),
  ], [lists]);
  const unknownList = (v: number) => listsState === "loading" ? `List #${v}` : `List #${v} (not found)`;

  const validDaySet = useMemo(() => {
    // Filter empties BEFORE Number(): "".split(",") yields [""], and
    // Number("") === 0 (not NaN), so the trailing NaN-filter let an empty
    // ValidDays string poison the set with 0 → "Mon" rendered as checked
    // when the user expected no days selected.
    return new Set(parseValidDays(promo.ValidDays || ""));
  }, [promo.ValidDays]);

  const toggleDay = (dayIdx: number) => {
    const s = new Set(validDaySet);
    if (s.has(dayIdx)) s.delete(dayIdx); else s.add(dayIdx);
    setField("ValidDays", Array.from(s).sort().join(","));
  };

  const firstTimeOnly = promo.ValidTimesFor === VALID_TIMES_FOR_FIRST_TIME;
  const toggleFirstTime = () => {
    if (firstTimeOnly) {
      setField("ValidTimesFor", VALID_TIMES_FOR_CODE);
      setField("ValidTimes", 0);
    } else {
      setField("ValidTimesFor", VALID_TIMES_FOR_FIRST_TIME);
      setField("ValidTimes", 1);
    }
  };
  const validTimesForOpts = getValidTimesForOptions(moduleId);

  const appliesToOpts = getAppliesToOptions(moduleId);
  const givesAsOpts = getGivesAsOptions(moduleId);
  const discountOnOpts = getDiscountOnOptions(moduleId);
  const showExtDays = showExtendedDays(moduleId);
  const useCounter = useCounterForMinCart(moduleId);

  return (
    <>
      {!isNew && (
        <div className={formStyles.fieldRow}>
          <label>Promotion ID</label>
          <input className={formStyles.fieldInput} value={promo.PromotionID} readOnly />
        </div>
      )}

      {/* Identity */}
      <div className={formStyles.section}>
        <div className={formStyles.sectionTitle}>Identity</div>
        <div className={formStyles.fieldRow}>
          <label>Name</label>
          <input className={formStyles.fieldInput} value={promo.Title}
            onChange={(e) => setField("Title", e.target.value)} />
        </div>
        {!ownerOperatorId && (
          <div className={formStyles.fieldRow}>
            <label>Operator</label>
            <OperatorSinglePicker value={promo.OperatorID}
              onChange={(id) => setField("OperatorID", id)} />
          </div>
        )}
        <div className={formStyles.fieldRow}>
          <label>Admin Promotion</label>
          <input type="checkbox" checked={promo.AdminPromotion}
            onChange={(e) => setField("AdminPromotion", e.target.checked)} />
        </div>
        <div className={formStyles.fieldRowStacked}>
          <label>Description</label>
          <textarea className={formStyles.fieldTextarea} value={promo.Description}
            onChange={(e) => setField("Description", e.target.value)} rows={3} />
        </div>
      </div>

      {/* Applies To */}
      {appliesToOpts && (
        <div className={formStyles.section}>
          <div className={formStyles.sectionTitle}>Applies To</div>
          <div className={formStyles.fieldRow}>
            <label>Applies to</label>
            <NumberSelect value={promo.PurchaseType} options={appliesToOpts}
              placeholder={{ v: PURCHASE_TYPE_UNSET, l: "-- Select --" }}
              onChange={(v) => setField("PurchaseType", v)} />
          </div>
        </div>
      )}

      {/* Location / Kiosk */}
      <div className={formStyles.section}>
        <div className={formStyles.sectionTitle}>Location</div>
        <div className={formStyles.fieldRow}>
          <label>Target</label>
          <select className={formStyles.fieldSelect} value={locationMode}
            onChange={(e) => setLocationMode(e.target.value as "kiosk" | "list")}>
            <option value="kiosk">Specific Kiosk</option>
            <option value="list">Location List</option>
          </select>
        </div>
        {locationMode === "kiosk" ? (
          <div className={formStyles.fieldRow}>
            <label>Kiosk ID</label>
            <input className={formStyles.fieldInput} type="number" value={promo.ValidOnKiosk}
              onChange={(e) => setField("ValidOnKiosk", parseInt(e.target.value) || 0)}
              placeholder="0 = all kiosks" />
          </div>
        ) : (
          <div className={formStyles.fieldRow}>
            <label>Location List</label>
            <NumberSelect value={promo.PromotionLocationListID} options={locationListOpts}
              unknownLabel={unknownList}
              onChange={(v) => setField("PromotionLocationListID", v)} />
          </div>
        )}
      </div>

      {/* Product List */}
      <div className={formStyles.section}>
        <div className={formStyles.sectionTitle}>Product List</div>
        <div className={formStyles.fieldRow}>
          <label>Product List</label>
          <NumberSelect value={promo.PromotionListID} options={productListOpts}
            unknownLabel={unknownList}
            onChange={(v) => setField("PromotionListID", v)} />
        </div>
      </div>

      {/* Usage */}
      <div className={formStyles.section}>
        <div className={formStyles.sectionTitle}>Usage</div>
        <div className={formStyles.fieldRow}>
          <label>First time only</label>
          <input type="checkbox" checked={firstTimeOnly} onChange={toggleFirstTime} />
        </div>
        {!firstTimeOnly && (
          <>
            <div className={formStyles.fieldRow}>
              <label>Can be used</label>
              <input className={formStyles.fieldInput} type="number" value={promo.ValidTimes}
                onChange={(e) => setField("ValidTimes", parseInt(e.target.value) || 0)}
                placeholder="0 = unlimited" />
            </div>
            <div className={formStyles.fieldRow}>
              <label>Time(s) per</label>
              <NumberSelect value={promo.ValidTimesFor} options={validTimesForOpts}
                onChange={(v) => setField("ValidTimesFor", v)} />
            </div>
          </>
        )}
      </div>

      {/* Discount */}
      <div className={formStyles.section}>
        <div className={formStyles.sectionTitle}>Discount</div>
        <div className={formStyles.fieldRow}>
          <label>Promotion gives</label>
          <input className={formStyles.fieldInput} type="number" value={promo.Discount}
            onChange={(e) => setField("Discount", parseFloat(e.target.value) || 0)} />
        </div>
        <div className={formStyles.fieldRow}>
          <label>As</label>
          <NumberSelect value={promo.DiscountType} options={givesAsOpts}
            onChange={(v) => setField("DiscountType", v)} />
        </div>
        {showExtDays && (
          <div className={formStyles.fieldRow}>
            <label>Extended days</label>
            <input className={formStyles.fieldInput} type="number" value={promo.ExtendedDays}
              onChange={(e) => setField("ExtendedDays", parseInt(e.target.value) || 0)} />
          </div>
        )}
        <div className={formStyles.fieldRow}>
          <label>Discount on</label>
          <NumberSelect value={promo.DiscountFor} options={discountOnOpts}
            onChange={(v) => setField("DiscountFor", v)} />
        </div>
        {useCounter ? (
          <div className={formStyles.fieldRow}>
            <label>Min cart</label>
            <input className={formStyles.fieldInput} type="number" value={promo.DiscountMinCart}
              onChange={(e) => setField("DiscountMinCart", parseInt(e.target.value) || 0)} min={0} />
          </div>
        ) : (
          <>
            <div className={formStyles.fieldRow}>
              <label>Min cart</label>
              <input className={formStyles.fieldInput} type="number" value={promo.DiscountMinCart}
                onChange={(e) => setField("DiscountMinCart", parseInt(e.target.value) || 0)} />
            </div>
            <div className={formStyles.fieldRow}>
              <label>Max cart</label>
              <input className={formStyles.fieldInput} type="number" value={promo.DiscountMaxCart}
                onChange={(e) => setField("DiscountMaxCart", parseInt(e.target.value) || 0)}
                placeholder="-1 = max items" />
            </div>
          </>
        )}
        <div className={formStyles.fieldRow}>
          <label>Apply to tax</label>
          <input type="checkbox" checked={promo.ApplyDiscountToTax}
            onChange={(e) => setField("ApplyDiscountToTax", e.target.checked)} />
        </div>
      </div>

      {/* Valid Days */}
      <div className={formStyles.section}>
        <div className={formStyles.sectionTitle}>Valid Days</div>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          {DAY_NAMES.map((day, i) => (
            <label key={i} style={{ display: "flex", alignItems: "center", gap: "var(--space-1)", fontSize: "var(--text-sm)" }}>
              <input type="checkbox" checked={validDaySet.has(i)} onChange={() => toggleDay(i)} />
              {day}
            </label>
          ))}
        </div>
      </div>
    </>
  );
}

/* ── Promotion modal (handles both Create + Edit) ────── */

export function PromotionModal({ promotionId, onClose, onSaved }: {
  promotionId: number; onClose: () => void; onSaved: () => void;
}) {
  const { cache, activeEndpoint, upsertPromotion } = useAuth();
  const { toast } = useToast();
  const isNew = !promotionId;
  const [promo, setPromo] = useState<Promotion | null>(null);
  const [lists, setLists] = useState<PromotionList[]>([]);
  const [listsState, setListsState] = useState<ListsState>("loading");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);
  const [locationMode, setLocationMode] = useState<"kiosk" | "list">("list");
  // Snapshot of the form as loaded; the modal is dirty while it differs.
  const [baseline, setBaseline] = useState<string | null>(null);
  const loadRef = useRef(0);

  const moduleId = activeEndpoint?.ModuleID ?? 0;
  const ownerOperatorId = cache?.OwnerOperatorID;

  useEffect(() => {
    getPromotionLists()
      .then((res) => {
        if (!res.bError && res.oData) { setLists(res.oData); setListsState("ready"); }
        else setListsState("failed");
      })
      .catch((err) => { console.error(err); setListsState("failed"); });
  }, []);

  useEffect(() => {
    if (isNew) {
      const p = emptyPromotion();
      p.OperatorID = ownerOperatorId ?? 0;
      const normalized = normalizePromotion(p, moduleId);
      setPromo(normalized);
      setLocationMode("list");
      setBaseline(snapshot(normalized, "list"));
      return;
    }
    if (promotionId <= 0) return;
    const id = ++loadRef.current;
    setLoading(true);
    getPromotionInfo(promotionId)
      .then((res) => {
        if (id !== loadRef.current) return;
        if (res.bError) { setStatus({ type: "error", message: res.sMessage ?? "Error loading." }); return; }
        if (res.oData) {
          const normalized = normalizePromotion(res.oData, moduleId);
          const mode = normalized.ValidOnKiosk > 0 ? "kiosk" : "list";
          setPromo(normalized);
          setLocationMode(mode);
          setBaseline(snapshot(normalized, mode));
        }
      })
      .catch((err) => setStatus({ type: "error", message: String(err) }))
      .finally(() => { if (id === loadRef.current) setLoading(false); });
  }, [promotionId, isNew, ownerOperatorId, moduleId]);

  const setField = <K extends keyof Promotion>(key: K, value: Promotion[K]) => {
    setPromo((prev) => prev ? { ...prev, [key]: value } : prev);
  };

  const dirty = promo != null && baseline != null && snapshot(promo, locationMode) !== baseline;

  const handleSave = useCallback(async () => {
    if (!promo) return;
    setStatus(null);
    const err = validatePromotion(promo, moduleId, locationMode, listsState === "ready" ? lists : null);
    if (err) { setStatus({ type: "error", message: err }); return; }

    const payload: Promotion = {
      ...promo,
      ValidOnKiosk: locationMode === "list" ? 0 : promo.ValidOnKiosk,
      PromotionLocationListID: locationMode === "list" ? promo.PromotionLocationListID : 0,
    };

    setSaving(true);
    try {
      const res = await savePromotionInfo(payload);
      if (res.bError) { setStatus({ type: "error", message: res.sMessage ?? "Error saving." }); return; }
      // SavePromotionInfo answers a rejected or failed write with no data and
      // no error. That is a failure: stay open with the user's input intact.
      if (!res.oData) {
        setStatus({
          type: "error",
          message: isNew
            ? "The server did not create the promotion. Check the values and try again."
            : "The server did not save the promotion. Check the values and try again.",
        });
        return;
      }
      // The grid route closes on onSaved; the PagePopup route stays open, so
      // show the saved record and treat it as the new clean state.
      const saved = normalizePromotion(res.oData, moduleId);
      const mode = saved.ValidOnKiosk > 0 ? "kiosk" : "list";
      setPromo(saved);
      setLocationMode(mode);
      setBaseline(snapshot(saved, mode));
      setStatus({ type: "success", message: isNew ? `Created — Promotion #${saved.PromotionID}` : "Saved." });
      upsertPromotion(res.oData);
      toast(isNew ? "Promotion created." : "Promotion saved.", "success");
      onSaved();
    } catch (e) {
      setStatus({ type: "error", message: String(e) });
    } finally {
      setSaving(false);
    }
  }, [promo, moduleId, locationMode, lists, listsState, isNew, toast, onSaved, upsertPromotion]);

  return (
    <GridEditModal
      title={isNew ? "New Promotion" : `Edit Promotion #${promotionId}`}
      wide
      loading={loading}
      saving={saving}
      statusMessage={status}
      saveLabel={isNew ? "Create" : "Save"}
      unsaved={dirty}
      onClose={onClose}
      onSave={handleSave}
    >
      {promo && (
        <div className={formStyles.formContainer}>
          <PromotionForm
            promo={promo}
            setField={setField}
            isNew={isNew}
            moduleId={moduleId}
            ownerOperatorId={ownerOperatorId}
            lists={lists}
            listsState={listsState}
            locationMode={locationMode}
            setLocationMode={setLocationMode}
          />
        </div>
      )}
    </GridEditModal>
  );
}

/* ── Main page ───────────────────────────────────────── */
export function SearchEditPromotionPage() {
  const gridRef = useRef<DataGridHandle | null>(null);
  const editor = useGridEditDialog({ gridRef, idColumn: "iPromotionID" });

  return (
    <SimplePageLayout title="Promotions">
      <DataGrid
        ref={gridRef}
        name="promotionSearch"
        onRowSelect={editor.handleRowSelect}
        onButtonClick={editor.handleButtonClick}
        toolbarExtra={
          <button className={formStyles.gridCreateBtn} onClick={editor.openCreate}>+ New Promotion</button>
        }
      />
      {editor.dialog && (
        <PromotionModal
          key={editor.dialog.kind === "edit" ? editor.dialog.id : "new"}
          promotionId={editor.dialog.kind === "edit" ? editor.dialog.id : 0}
          onClose={editor.close}
          onSaved={editor.onSaved}
        />
      )}
    </SimplePageLayout>
  );
}
